import { ButtonType, ButtonWidget, DialogType, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import { Formik, useFormikContext } from 'formik';
import React from 'react';

import { useSettingsCoordinator } from '~settings-coordinator/SettingsCoordinatorHook';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { OAuthCredentialsService } from '~src/connectivity/services/OAuthCredentialsService';

import { Messages } from '../../../../../codegen/Localize';
import { ModalInstance, ReactModalService } from '../../../../../commonInfra/dialogs/ReactModalService';
import { IResult } from '../../../../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../../../../httpClient/WebHttpClient';
import { AddOAuthClientParams, AddOAuthClientsRequest } from '../../../../../ts/ServerApi/requests/AddOAuthClientsRequest';
import { DeleteOAuthClientsRequest } from '../../../../../ts/ServerApi/requests/DeleteOAuthClientsRequest';
import { GetOAuthClientsRequest, IGetOAuthClientsResult } from '../../../../../ts/ServerApi/requests/GetOAuthClientsRequest';
import { IOAuthClient } from '../../../../../ts/ServerApi/types/IOAuthClient';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { learnMoreStyle } from '../../../../styles/CommonStyles';
import { addOAuthClientDialogStyle, buttonStyle } from '../../../styles/oauth-client/OAuthClientsRegistryStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import {
  AddOAuthClientDialog,
  AddOAuthClientDialogProps,
  AddOAuthClientDialogState,
  OAuthClientSetting,
  OAuthClientsFormField,
  OAuthClientsFormikProps
} from './AddOAuthClientDialog';

interface AddOAuthClientResult {
  id: string;
  errorMessage: string;
}
interface AddOAuthClientsResult {
  addOAuthClientsResult: AddOAuthClientResult[];
}

function getDescription() {
  return <>
    <span data-tb-test-id='oauth-client-registry-description'>
      {Messages.oauthClientsRegistryDescription()}
      <TextLinkWidget
        testId='oauth-client-registry-learn-more'
        className={learnMoreStyle}
        kind={TextLinkKind.Standard}
        href={HelpService.getHelpUrlByCategory('server-oauth-registry')}
        target={'_blank'}
        rel={'noreferrer noopener'}
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    </span>
  </>;
}

export function renderOAuthClientListTableHeader(showInstanceUrlColumn: boolean) {
  const tableHeaders = showInstanceUrlColumn ?
    { [Messages.oauthClientDbClass()]: '', [Messages.oauthClientInstanceUrl()]: '', [Messages.oauthClientId()]: '', '': '' } :
    { [Messages.oauthClientDbClass()]: '', [Messages.oauthClientId()]: '', '': '' };
  let header = Object.keys(tableHeaders);
  return header.map((key, index) => {
    return (<th key={index} className='tb-data-grid-headers-line'>
      <span className='tb-data-grid-header-text'>{key}</span>
    </th>);
  });
}


export async function fetchOAuthClients(): Promise<IGetOAuthClientsResult> {
  return WebHttpClient.sendRequest(new GetOAuthClientsRequest({}));
}

export async function deleteOAuthClients(toDeleteIds: string[]): Promise<IResult> {
  return WebHttpClient.sendRequest(new DeleteOAuthClientsRequest(toDeleteIds));
}

export async function addOAuthClients(toAddClients: AddOAuthClientParams[]): Promise<IResult> {
  return WebHttpClient.sendRequest(new AddOAuthClientsRequest({ oauthClients: toAddClients }));
}

/**
 * Component with dependencies injected as props
 */
export function OAuthClientsRegistrySection() {
  const [oauthClients, setOAuthClients] = React.useState<OAuthClientSetting[]>([]);
  const initialValues = React.useMemo(() => {
    return { oauthClients };
  }, [oauthClients]);

  React.useEffect(() => updateOAuthClients(), []);

  function updateOAuthClients() {
    fetchOAuthClients().then((result: { oauthClients: IOAuthClient[] }) => {
      const clientSettings = result.oauthClients.map((v: IOAuthClient) => {
        const clientSetting: OAuthClientSetting = {
          dbClass: v.type,
          instanceUrl: v.instanceUrl ?? '',
          oauthClientId: v.clientId,
          oauthClientSecret: '',
          oauthClientRedirectUri: '',
          id: v.id
        };
        return clientSetting;
      });
      setOAuthClients(clientSettings);
    });
  }

  const handleSaveAction = React.useCallback(async (values: OAuthClientsFormField, formik: OAuthClientsFormikProps) => {
    let toAddClients: AddOAuthClientParams[] = [];
    values.oauthClients
      .filter((client: OAuthClientSetting) => client.oauthClientSecret !== '')
      .map((client: OAuthClientSetting) => {
        toAddClients.push({
          'dbClass': client.dbClass,
          'instanceUrl': client.instanceUrl,
          'clientId': client.oauthClientId,
          'clientSecret': client.oauthClientSecret,
          'redirectUri': client.oauthClientRedirectUri
        });
      });

    const promises: Promise<IResult>[] = [];

    // the difference between initial id set and the current one are the ids to be deleted
    let origIds = initialValues.oauthClients.filter((client: OAuthClientSetting) => client.id !== '').map((client: OAuthClientSetting) => client.id);
    let newIds = values.oauthClients.filter((client: OAuthClientSetting) => client.id !== '').map((client: OAuthClientSetting) => client.id);
    let toDeleteIds = origIds.filter((id: string) => newIds.indexOf(id) < 0);

    if (toAddClients.length) {
      const addPromise = addOAuthClients(toAddClients);
      promises.push(addPromise);
    }
    if (toDeleteIds.length) {
      const delPromise = deleteOAuthClients(toDeleteIds);
      promises.push(delPromise);
    }

    if (promises.length) {
      try {
        const result = await Promise.all(promises);
        const addClientResults = result[0] as AddOAuthClientsResult;
        const failedAdd = addClientResults.addOAuthClientsResult?.filter((x: AddOAuthClientResult) => x.id === '-1');
        if (failedAdd?.length) {
          const errMsgs = failedAdd.map(x => x.errorMessage);
          throw errMsgs;
        }
      } finally {
        updateOAuthClients();
      }
    }
  }, [initialValues.oauthClients]);

  return (
    <SettingsSection title={Messages.oauthClientsRegistry()}
      description={getDescription()}
      testId='oauth-clients-registry-site-setting'>
      <div>
        <Formik initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={(values: OAuthClientsFormField, formik: OAuthClientsFormikProps) => handleSaveAction(values, formik)}>
          <FormContent />
        </Formik>
      </div>
    </SettingsSection>
  );
}

function useAddOAuthClientSubmit() {
  const formik = useFormikContext<OAuthClientsFormField>();
  return React.useCallback(
    (client: AddOAuthClientDialogState) => {
      const clients = formik.values.oauthClients.filter((c: OAuthClientSetting) => (c.dbClass !== client.dbClass || c.instanceUrl !== client.instanceUrl));
      clients.push({
        dbClass: client.dbClass,
        instanceUrl: client.instanceUrl,
        oauthClientId: client.oauthClientId,
        oauthClientSecret: client.oauthClientSecret,
        oauthClientRedirectUri: client.oauthClientRedirectUri,
        id: Math.random().toString(36).substring(2)
      });
      formik.setFieldValue('oauthClients', clients, false);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik, formik.setFieldValue]
  );
}

export function useOpenAddOAuthClientDialog() {
  const formik = useFormikContext<OAuthClientsFormField>();
  const handleAddOAuthClientSubmit = useAddOAuthClientSubmit();
  return React.useCallback(
    (): Promise<IResult> => {
      let modalInstance: ModalInstance;
      const dialogProps: AddOAuthClientDialogProps = {
        dismiss: () => modalInstance && modalInstance.dismiss(),
        close: (result: any) => modalInstance && modalInstance.close(result),
        addOAuthClientSubmit: handleAddOAuthClientSubmit,
        showInstanceUrlSection: true,
      };

      modalInstance = ReactModalService.open({
        type: DialogType.Uber,
        dialogContent: React.createElement(AddOAuthClientDialog, dialogProps),
        testId: 'addOAuthClientDialog',
        showCloseButton: true,
        dialogStyle: addOAuthClientDialogStyle,
        shouldNotSetDefaultWidth: true
      });
      return modalInstance.result;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik]
  );
}

export function FormContent() {
  useSettingsCoordinator();
  const formik = useFormikContext<OAuthClientsFormField>();
  const handleOpenAddOAuthClientDialog = useOpenAddOAuthClientDialog();

  return <>
    <div>
      <ButtonWidget buttonType={ButtonType.Outline}
        testId='add-oauth-client-on-site-button'
        className='tb-outline-button tb-margin-left-wide'
        onClick={() => handleOpenAddOAuthClientDialog()}
        disabled={false}
        type='button'>{Messages.addOAuthClient_action()}
      </ButtonWidget>
    </div>
    <p />
    {(formik.values.oauthClients.length > 0 || formik.dirty) &&
      <div>
        <table id='oauth-clients-list-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
          <thead>
            <tr data-test-id='create-oauth-client-db-class'>
              {renderOAuthClientListTableHeader(true)}
            </tr>
          </thead>
          <tbody id='oauth-clients-list-body-id'>
            {formik.values.oauthClients.map((client: OAuthClientSetting) => {
              const props: TableRowProps = {
                client,
                deleteClient: (client: OAuthClientSetting) => { handleDeleteClient(client, formik); },
                showInstanceUrlColumn: true
              };
              return renderOAuthClientTableRow(props, false, false);
            })}
          </tbody>
        </table>
      </div>
    }
  </>;
}

export function handleDeleteClient(client: OAuthClientSetting, formik: OAuthClientsFormikProps) {
  const oauthClients = formik.values.oauthClients;
  const index = oauthClients.indexOf(client, 0);
  if (index > -1) {
    const clients = [...oauthClients];
    clients.splice(index, 1);
    formik.setFieldValue('oauthClients', clients, false);
  }
}

interface TableRowProps {
  client: OAuthClientSetting;
  deleteClient: (client: OAuthClientSetting) => void;
  showInstanceUrlColumn: boolean;
}

export function renderOAuthClientTableRow(props: TableRowProps, emptyState: boolean, deleteButtonDisabled: boolean, emptyStateMessage?: string) {
  return (
    <tr key={props.client ? props.client.id : 'empty-state'}>
      <td><span id='oauth-client-dbclass' className='tb-data-grid-cell-text tb-lr-padded-wide'>{emptyState ? emptyStateMessage : OAuthCredentialsService.getDisplayStrings(props.client.dbClass).displayName}</span></td>
      {props.showInstanceUrlColumn && <td><span className='tb-data-grid-cell-text tb-lr-padded-wide'>{props.client.instanceUrl}</span></td>}
      {!emptyState && <>
        <td><span className='tb-data-grid-cell-text tb-lr-padded-wide'>{props.client.oauthClientId}</span></td>
        <td>
          <span className='tb-data-grid-cell-text tb-lr-padded-wide'>
            <ButtonWidget buttonType={ButtonType.LowEmphasis}
              testId='delete-oauth-client-on-site-button'
              className={buttonStyle}
              onClick={() => props.deleteClient(props.client)}
              disabled={deleteButtonDisabled}
              type='button'>{Messages.oauthDeleteClient()}
            </ButtonWidget>
          </span>
        </td>
      </>}
    </tr>
  );
}

export const OAuthClientsRegistrySectionSpecs = componentRegistryEntry({
  name: 'OAuthClientsRegistrySection',
  component: OAuthClientsRegistrySection
});
